import type { Config } from '@ms/yammer-libs-static-config';
import {
  clearAllTelemetryEvents,
  drainPreConfigBuffer,
  getSkipCleanupFlag,
  removeClientContext,
  removeFirstMeaningfulPaintContext,
  removeScenarioPerformanceCollection,
  removeUserContext,
  setClientContext,
  setSkipCleanupFlag,
  setTelemetryConfig,
} from '@ms/yammer-telemetry-store';
import type { ClientContext, HostingConfig, TelemetryClientId } from '@ms/yammer-telemetry-support';

import { reportError } from '../api';
import type { TelemetryEvent } from '../events';
import { configurePerformanceObserver } from '../performance';
import { emitTelemetryEvent, routePerformanceBatch } from '../report/eventSink';

import { addUnhandledErrorEventListener, addUnhandledRejectionEventListener } from './addEventListeners';
import { addPageHideQueueObservationEventListener } from './addPageHideQueueObservationEventListener';

const isBrowserPerformanceApi = (performanceApi: unknown): performanceApi is Partial<Performance> =>
  typeof performanceApi === 'object' && performanceApi != null;

const getBrowserPerformanceApi = () => {
  const performanceApi: unknown = Reflect.get(window, 'performance');

  return isBrowserPerformanceApi(performanceApi) ? performanceApi : undefined;
};

const isBrowserPerformanceMarkAvailable = () => typeof getBrowserPerformanceApi()?.mark === 'function';

const checkIfBrowserPerformanceApiAvailable = () => {
  const isAvailable = isBrowserPerformanceMarkAvailable();

  if (!isAvailable) {
    reportError({
      error: new Error('window.performance object is not available'),
      eventProperties: { errorCode: 'PerformanceApiUnavailable' },
    });
  }

  return isAvailable;
};

interface ConfigureTelemetryOptions {
  /**
   * Client configuration
   */
  readonly config: Config;

  /**
   * Hosting service configuration
   */
  readonly hostingConfig: HostingConfig;

  /**
   * It identifies which client application is running.
   * It is primarily used for telemetry and analytics reporting, allowing backend systems to distinguish where events are coming from
   */
  readonly telemetryClientId: TelemetryClientId;

  /**
   * Whether or not to skip clearing all queued events and user context from session storage the next time
   * telemetry is configured. You may want to set this to true if you are calling configure from a bootstrap
   * sequence that reloads the page for the same user when successful. Set to false by default.
   */
  readonly skipNextCleanup?: boolean;

  /**
   * Whether or not to register an event listener for unhandled errors or rejections.
   */
  readonly registerUnhandledEventsListener?: boolean;
  readonly isMsal?: boolean;
  readonly clientContext?: ClientContext;

  /**
   * If protobuf (analytics v2) events should be logged to the console before they're sent to the api/v3/events endpoint.
   */
  readonly shouldLogProtobufEventsToConsole?: boolean;

  /**
   * If true, the PerformanceObserver will not be used to collect performance data.
   */
  readonly skipConfigPerformanceObserver?: boolean;

  /**
   * If true, reporting of long animation frame timing will be enabled in the performance observer.
   */
  readonly enableLongAnimationFrameTimingReporting?: boolean;

  /**
   * If true, allowlisted analytics events will be forwarded to registered custom data store callbacks
   */
  readonly enableReportingToCustomDataStore?: boolean;

  /**
   * If true, queued legacy Log events will be included in the bounded pagehide keepalive request.
   */
  readonly enableLogPagehideDelivery?: boolean;
}

type ConfigureTelemetry = (configureOptions: ConfigureTelemetryOptions) => void;

export const configureTelemetry: ConfigureTelemetry = ({
  config,
  hostingConfig,
  clientContext,
  telemetryClientId,
  skipNextCleanup = false,
  registerUnhandledEventsListener = false,
  isMsal = false,
  shouldLogProtobufEventsToConsole = false,
  skipConfigPerformanceObserver = false,
  enableLongAnimationFrameTimingReporting = false,
  enableReportingToCustomDataStore = false,
  enableLogPagehideDelivery = false,
}) => {
  if (!getSkipCleanupFlag()) {
    clearAllTelemetryEvents();
    removeUserContext();
    removeClientContext();
    removeFirstMeaningfulPaintContext();
  }

  // Make sure the scenario performance collection is clean on every boot
  // since the performance marks are always gone.
  removeScenarioPerformanceCollection();

  const currentTimeTicks = Date.now();
  const randomPerTabSalt = Math.floor(Math.random() * 10000000);
  const clientConfig = {
    ...config.telemetry,
    telemetryClientId,
    appInstanceId: `${currentTimeTicks}.${randomPerTabSalt}`,
    loadedAt: currentTimeTicks,
    resource: isMsal ? config.msal.resources.yammer : config.adal.resources.yammer,
    graphqlHost: config.hosts.graphql,
    telemetryHost: config.hosts.telemetry,
    shouldLogProtobufEventsToConsole,
    oneDSHost: config.hosts.oneDS,
    enableReportingToCustomDataStore,
    enableLogPagehideDelivery,
  };

  setTelemetryConfig(clientConfig, hostingConfig);
  setSkipCleanupFlag(skipNextCleanup);

  if (registerUnhandledEventsListener) {
    addUnhandledErrorEventListener();
    addUnhandledRejectionEventListener();
  }

  addPageHideQueueObservationEventListener(enableLogPagehideDelivery);

  const isBrowserPerformanceApiAvailable = checkIfBrowserPerformanceApiAvailable();

  if (!skipConfigPerformanceObserver && isBrowserPerformanceApiAvailable) {
    configurePerformanceObserver({
      enableLongAnimationFrameTimingReporting,
      reportCallback: (events) => {
        void routePerformanceBatch(events);
      },
    });
  }

  if (clientContext) {
    setClientContext(clientContext);
  }

  if (document.prerendering) {
    const restoreTelemetryStoreAfterActivation = () => {
      setTelemetryConfig(clientConfig, hostingConfig);
      if (clientContext) {
        setClientContext(clientContext);
      }
      setSkipCleanupFlag(skipNextCleanup);
    };

    document.addEventListener('prerenderingchange', restoreTelemetryStoreAfterActivation, {
      once: true,
    });
  }

  for (const event of drainPreConfigBuffer<TelemetryEvent>()) {
    void emitTelemetryEvent(event);
  }
};
